import { Box, Stack, SxProps, Theme } from "@mui/material";
import { PropsWithChildren, ReactElement, useEffect, useMemo, useState } from "react";
import { MediaType } from "../types";
import { useVideoPlayer, VideoPlayerProps } from "../VideoPlayer/VideoPlayer";
import { CarouselIndicator, MediaNavigation, MediaNavigationProps } from "./MediaNavigation";
import { useImagePreview, UseImagePreviewProps } from "./useImagePreview";
import { useUnsupportedPreview, UseUnsupportedPreviewProps } from "./useUnsupportedPreview";

export type PreviewProps =
  | ({ mediaType: MediaType.Image } & UseImagePreviewProps)
  | ({ mediaType: MediaType.Video } & VideoPlayerProps)
  | ({
      mediaType: MediaType.Unsupported | MediaType.Audio | MediaType.PDF;
    } & UseUnsupportedPreviewProps);

export type NavigationProps = Omit<
  MediaNavigationProps,
  "Media" | "Controls" | "fullscreenMode" | "playing" | "showFullscreenControl"
> & { position?: number; totalCount?: number };

export type MediaPreviewProps = {
  navigation: Omit<NavigationProps, "Summary">;
  fullScreenMode?: boolean;
  onClickFullscreen?: () => void;
  // FIXME: Ideally this would be part of the `navigation` prop too but... Storybook
  // https://github.com/storybookjs/storybook/issues/17720
  Summary?: ReactElement | null;
  sx?: SxProps<Theme>;
} & PreviewProps;

type ControlsContainerProps = {
  mediaType: MediaType;
  position?: number;
  totalCount?: number;
};

const ControlsContainer = ({
  mediaType,
  position,
  totalCount,
  children,
}: PropsWithChildren<ControlsContainerProps>): ReactElement => {
  const containerStyle: SxProps<Theme> | undefined = useMemo(() => {
    if (mediaType === MediaType.Video || mediaType === MediaType.Audio) return undefined;

    return {
      display: { xs: "grid", sm: "block" },
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "center",
      ".CarouselIndicator": { gridColumnStart: 2, pt: 0 },
    };
  }, [mediaType]);

  return (
    <Box sx={containerStyle}>
      {position && totalCount ? (
        <Stack className="CarouselIndicator" sx={{ flex: 1, pt: 0.5, display: { sm: "none" } }}>
          <CarouselIndicator position={position} totalCount={totalCount} />
        </Stack>
      ) : null}
      {children}
    </Box>
  );
};

type ImagePreviewProps = {
  previewProps: UseImagePreviewProps;
  navigation: NavigationProps;
};

const ImagePreview = ({ previewProps, navigation }: ImagePreviewProps) => {
  const [Media, Controls, state] = useImagePreview(previewProps);
  const { position, totalCount, ...props } = navigation;
  const { fullScreenMode } = previewProps;

  return (
    <MediaNavigation
      {...props}
      showFullscreenControl={state.showFullscreenControl}
      fullscreenMode={fullScreenMode}
      onMediaClick={fullScreenMode ? () => state.toggleFullscreenControl() : undefined}
      Media={Media}
      Controls={
        <ControlsContainer mediaType={MediaType.Image} position={position} totalCount={totalCount}>
          {Controls}
        </ControlsContainer>
      }
    />
  );
};

type VideoPreviewProps = {
  previewProps: VideoPlayerProps;
  navigation: NavigationProps;
};

const VideoPreview = ({ previewProps, navigation }: VideoPreviewProps) => {
  const [Media, Controls, state] = useVideoPlayer(previewProps);
  const { position, totalCount, ...props } = navigation;

  return (
    <MediaNavigation
      {...props}
      playing={state.playing}
      showFullscreenControl={state.showFullscreenControl}
      fullscreenMode={previewProps.fullScreenMode}
      Media={Media}
      Controls={
        <ControlsContainer mediaType={MediaType.Video} position={position} totalCount={totalCount}>
          {Controls}
        </ControlsContainer>
      }
    />
  );
};

type UnsupportedPreviewProps = {
  previewProps: { placeholder: string; fullScreenMode: boolean };
  navigation: NavigationProps;
};

const UnsupportedPreview = ({ previewProps, navigation }: UnsupportedPreviewProps) => {
  const [Media, Controls, state] = useUnsupportedPreview(previewProps);
  const { fullScreenMode } = previewProps;
  const { position, totalCount, ...props } = navigation;

  return (
    <MediaNavigation
      {...props}
      showFullscreenControl={state.showFullscreenControl}
      fullscreenMode={fullScreenMode}
      onMediaClick={fullScreenMode ? () => state.toggleFullscreenControl() : undefined}
      Media={Media}
      Controls={
        <ControlsContainer
          mediaType={MediaType.Unsupported}
          position={position}
          totalCount={totalCount}
        >
          {Controls}
        </ControlsContainer>
      }
    />
  );
};

export const MediaPreview = ({
  mediaType,
  navigation,
  Summary,
  sx,
  ...previewProps
}: MediaPreviewProps): ReactElement => {
  const [error, setError] = useState<ErrorEvent | undefined>();

  const { src } = previewProps as
    | ImagePreviewProps["previewProps"]
    | VideoPreviewProps["previewProps"];
  useEffect(() => {
    setError(undefined);
  }, [src]);

  return useMemo(() => {
    if (error) {
      return (
        <UnsupportedPreview
          navigation={{ ...navigation, Summary, sx }}
          previewProps={previewProps as unknown as UnsupportedPreviewProps["previewProps"]}
        />
      );
    }
    switch (mediaType) {
      case MediaType.Image:
        return (
          <ImagePreview
            navigation={{ ...navigation, Summary, sx }}
            previewProps={
              {
                ...previewProps,
                onError: setError,
              } as unknown as ImagePreviewProps["previewProps"]
            }
          />
        );
      case MediaType.Video:
        return (
          <VideoPreview
            navigation={{ ...navigation, Summary, sx }}
            previewProps={
              {
                ...previewProps,
                onError: setError,
              } as unknown as VideoPreviewProps["previewProps"]
            }
          />
        );
      default:
        return (
          <UnsupportedPreview
            navigation={{ ...navigation, Summary, sx }}
            previewProps={previewProps as unknown as UnsupportedPreviewProps["previewProps"]}
          />
        );
    }
  }, [error, mediaType, navigation, Summary, sx, previewProps]);
};
